import { BookingCard } from "@/components/booking/BookingCard";
import { getJobCategoryBadge } from "@/components/booking/booking-card-utils";
import { EmptyState } from "@/components/common/EmptyState";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useBookings } from "@/hooks/queries/use-bookings-query";
import { queryKeys } from "@/lib/query-keys";
import { Booking } from "@/lib/services/bookings";
import driverService, { ScheduledJob } from "@/lib/services/drivers";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

type Segment = "available" | "upcoming";

const segmentOptions: { label: string; value: Segment }[] = [
  { label: "Available", value: "available" },
  { label: "My upcoming", value: "upcoming" },
];

function ScheduleBanner({
  scheduledAt,
  bidAmount,
}: {
  scheduledAt?: string | null;
  bidAmount?: number | null;
}) {
  const badge = getJobCategoryBadge("scheduled", scheduledAt);

  if (!badge) {
    return null;
  }

  return (
    <View style={[styles.banner, { backgroundColor: badge.backgroundColor }]}>
      <Ionicons name={badge.icon} size={16} color={badge.color} />
      <ThemedText
        style={[styles.bannerText, { color: badge.color }]}
        type="smallSemiBold"
      >
        {badge.description}
      </ThemedText>
      {bidAmount != null ? (
        <ThemedText
          style={[styles.bannerText, { color: badge.color }]}
          type="extraSmallSemiBold"
        >
          Bid sent £{Number(bidAmount).toFixed(2)}
        </ThemedText>
      ) : null}
    </View>
  );
}

export default function ScheduledJobsScreen() {
  const router = useRouter();
  const { theme } = useAuth();
  const [segment, setSegment] = useState<Segment>("available");

  const available = useQuery({
    queryKey: queryKeys.bookings.scheduledJobs(),
    queryFn: () => driverService.getScheduledJobs(),
    enabled: segment === "available",
  });
  const upcoming = useBookings(
    { status: "scheduled" },
    { enabled: segment === "upcoming" },
  );

  const activeQuery = segment === "available" ? available : upcoming;

  const renderAvailableJob = useCallback(
    ({ item }: { item: ScheduledJob }) => (
      <View style={styles.item}>
        <ScheduleBanner
          scheduledAt={item.scheduled_at}
          bidAmount={item.my_quote?.amount}
        />
        <BookingCard
          booking={item.booking}
          backgroundColor={theme.card}
          borderColor={theme.border}
          onPress={() =>
            router.push(
              `/driver-booking-response/${item.booking_id}?vehicleId=${item.vehicle_id}`,
            )
          }
        />
      </View>
    ),
    [router, theme.border, theme.card],
  );

  const renderUpcomingBooking = useCallback(
    ({ item }: { item: Booking }) => (
      <View style={styles.item}>
        <ScheduleBanner scheduledAt={item.scheduled_at} />
        <BookingCard
          booking={item}
          backgroundColor={theme.card}
          borderColor={theme.border}
          onPress={() => router.push(`/bookings/${item.id}`)}
        />
      </View>
    ),
    [router, theme.border, theme.card],
  );

  const refreshControl = (
    <RefreshControl
      refreshing={activeQuery.isRefetching}
      onRefresh={() => activeQuery.refetch()}
      tintColor={BrandColors.primary}
    />
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader title="Scheduled Jobs" />
      <View
        style={[styles.filterContainer, { borderBottomColor: theme.border }]}
      >
        {segmentOptions.map((option) => {
          const isActive = segment === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={styles.filterTab}
              onPress={() => setSegment(option.value)}
              activeOpacity={0.7}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={`${option.label} scheduled jobs`}
              testID={`scheduled_jobs_${option.value}_tab`}
            >
              <ThemedText
                style={[styles.filterText, isActive && styles.filterTextActive]}
                type="smallSemiBold"
              >
                {option.label}
              </ThemedText>
              {isActive ? <View style={styles.filterIndicator} /> : null}
            </TouchableOpacity>
          );
        })}
      </View>

      {activeQuery.isLoading ? (
        <Loader inline />
      ) : activeQuery.isError ? (
        <EmptyState
          icon="alert-circle-outline"
          title="Couldn't load scheduled jobs"
          subtitle="Check your connection and try again."
          actionLabel="Retry"
          onAction={() => activeQuery.refetch()}
        />
      ) : segment === "available" ? (
        <FlatList
          data={available.data ?? []}
          renderItem={renderAvailableJob}
          keyExtractor={(item) => item.booking_id.toString()}
          contentContainerStyle={styles.listContent}
          refreshControl={refreshControl}
          ListEmptyComponent={
            <EmptyState
              icon="calendar-outline"
              title="No scheduled jobs available"
              subtitle="Future jobs near you that match your vehicles will appear here."
            />
          }
        />
      ) : (
        <FlatList
          data={upcoming.data?.data ?? []}
          renderItem={renderUpcomingBooking}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContent}
          refreshControl={refreshControl}
          ListEmptyComponent={
            <EmptyState
              icon="calendar-outline"
              title="No upcoming jobs"
              subtitle="Scheduled jobs you have accepted will appear here until they start."
            />
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
  },
  filterContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    marginBottom: 4,
  },
  filterTab: {
    flex: 1,
    alignItems: "center",
    paddingTop: 12,
    paddingBottom: 10,
    position: "relative",
  },
  filterText: {
    color: BrandColors.mediumGray,
    fontSize: 14,
  },
  filterTextActive: {
    color: BrandColors.primary,
  },
  filterIndicator: {
    position: "absolute",
    bottom: 0,
    left: 8,
    right: 8,
    height: 3,
    borderRadius: 2,
    backgroundColor: BrandColors.primary,
  },
  listContent: {
    padding: 16,
    paddingTop: 12,
  },
  item: {
    gap: 6,
  },
  banner: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  bannerText: {
    fontSize: 13,
  },
});
